import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { FC } from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Radio,
  Stack,
  ToggleButton,
  Typography,
} from "@mui/material";
import { useLanguage } from "@tlc/ls2pac/components/Language";
import { useAccount } from "@tlc/ls2pac/components/Account";
import {
  AllListsSummary,
  ListService,
  ListType,
} from "../../account/ListService";
import { LoginClientManager } from "@tlc/ls2pac/app/ClientManagers/LoginClientManager";
import CheckIcon from "@mui/icons-material/Check";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { FormTextField } from "@tlc/forms/FormTextField";
import { HigherContrastToggleButtonGroup } from "@tlc/ls2pac/components/ResultsViewToggle";
import { ChildCheckboxGroup } from "../../account/lists/ChildSelectionDialog";
import { useToast } from "@tlc/ui/toast";
import { useIsMobile } from "@tlc/ui/useIsMobile";
import { useSupportedFunctionality } from "@tlc/ls2pac/components/SupportedFunctionality";
import { BorrowerChild } from "@tlc/ls2pac/dto/BorrowerChild";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import { useAnalytics } from "../../../../analytics/useGoogleAnalytics";
import { useDialog } from "@tlc/ui/dialog/useDialog";

const ADD_TO_LIST_DIALOG_WIDTH = "440px";

export function useListTypeLabel(): Record<ListType, string> {
  const { translate } = useLanguage();
  return {
    [ListType.Prohibited]: translate("prohibited"),
    [ListType.Config]: translate("config"),
    [ListType.Personal]: translate("personal"),
  };
}

export function useAddToListResponseHandler() {
  const { translate } = useLanguage();
  const { showToast } = useToast();

  return ({
    totalEResourcesNotAdded,
    successCount,
  }: {
    success: boolean;
    totalEResourcesNotAdded: number;
    successCount: number;
  }) => {
    // Prohibited lists don't support eResources
    if (totalEResourcesNotAdded > 0 && successCount === 0) {
      showToast(
        `${translate("addToProhibitedListError", {
          ERROR_COUNT: totalEResourcesNotAdded.toString(),
        })} ${translate("addToProhibitedListEResourceError")}`,
        "error",
      );
    }

    if (totalEResourcesNotAdded > 0 && successCount > 0) {
      showToast(
        <>
          {translate("addToProhibitedListSuccess", {
            SUCCESS_COUNT: successCount.toString(),
          })}
          <br />
          <br />
          {translate("addToProhibitedListError", {
            ERROR_COUNT: totalEResourcesNotAdded.toString(),
          })}{" "}
          {translate("addToProhibitedListEResourceError")}
        </>,
        "info",
      );
    }

    if (successCount > 0 && totalEResourcesNotAdded === 0) {
      // display a success toast
      showToast(
        translate("addToProhibitedListSuccess", {
          SUCCESS_COUNT: successCount.toString(),
        }),
        "success",
      );
    }
    if (successCount === 0 && totalEResourcesNotAdded === 0) {
      showToast(translate("addToListErrorMessage"), "error");
    }
  };
}

interface CreateListFormInput {
  listName: string;
  type: ListType;
  childIds: BorrowerChild["id"][];
}
interface CreateListFormProps {
  resourceIds: number[];
  defaultName?: string;
  onClickBack: () => void;
  onClose: () => void;
}
export const CreateListForm: React.FC<CreateListFormProps> = ({
  defaultName,
  resourceIds,
  onClickBack,
  onClose,
}) => {
  const { supportedFunctionality } = useSupportedFunctionality();
  const { useGuardianProhibition } = supportedFunctionality || {};
  const { translate } = useLanguage();
  const listTypeLabel = useListTypeLabel();
  const isMobile = useIsMobile();
  const handleResponse = useAddToListResponseHandler();
  const { trackEvent } = useAnalytics();
  const {
    patron,
    isConfigAdmin,
    hasChildrenAndProhibitedListFunctionality,
    isAuthenticated,
  } = useAccount();
  const defaultType = useMemo(() => {
    if (hasChildrenAndProhibitedListFunctionality) {
      return ListType.Prohibited;
    }
    if (isConfigAdmin) {
      return ListType.Config;
    }
    return ListType.Personal;
  }, [
    isConfigAdmin,
    hasChildrenAndProhibitedListFunctionality,
    useGuardianProhibition,
  ]);

  const listTypes = useMemo(() => {
    return Object.values(ListType).filter(
      (lt) =>
        lt === ListType.Personal ||
        (lt === ListType.Config && isConfigAdmin) ||
        (lt === ListType.Prohibited &&
          useGuardianProhibition &&
          hasChildrenAndProhibitedListFunctionality),
    );
  }, [
    isConfigAdmin,
    hasChildrenAndProhibitedListFunctionality,
    useGuardianProhibition,
  ]);

  const formMethods = useForm<CreateListFormInput>({
    mode: "onBlur",
    defaultValues: {
      listName: defaultName || undefined,
      type: defaultType,
      childIds: [],
    },
  });

  const { handleSubmit, setValue } = formMethods;
  const type = useWatch({ name: "type", control: formMethods.control });

  const onSubmit = async ({
    listName,
    type,
    childIds,
  }: CreateListFormInput) => {
    const response = await ListService.createList(
      listName,
      type,
      resourceIds,
      childIds,
    );
    trackEvent("list_create", {
      list_type: type,
      item_count: resourceIds.length,
      default_list: false,
      batch: resourceIds.length > 1,
    });
    handleResponse(response);
    onClose();
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <FormProvider {...formMethods}>
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          <IconButton onClick={() => onClickBack()} aria-label="Back">
            <ArrowBackIosNewIcon />
          </IconButton>{" "}
          {translate("createNewList")}
        </DialogTitle>
        <DialogContent
          sx={{ width: isMobile ? "auto" : ADD_TO_LIST_DIALOG_WIDTH }}
        >
          <Stack paddingTop={2}>
            <FormTextField<CreateListFormInput>
              required
              name={"listName"}
              label={"List Name"}
              maxLength={255}
            />
            {listTypes.length > 1 && (
              <Controller<CreateListFormInput, "type">
                render={({ field, fieldState, formState }) => {
                  return (
                    <FormControl component="fieldset" sx={{ mt: 2 }}>
                      <FormLabel id="list-type-label" component={"legend"}>
                        List Type
                      </FormLabel>
                      <HigherContrastToggleButtonGroup
                        color="secondary"
                        exclusive
                        size="small"
                        aria-labelledby="list-type-label"
                        value={field.value}
                        onChange={(
                          _: React.MouseEvent<HTMLElement>,
                          newValue: ListType | null,
                        ) => {
                          // don't allow deselection
                          if (newValue != null) {
                            setValue("childIds", []); // reset childIds when changing tabs
                            field.onChange(newValue);
                          }
                        }}
                      >
                        {listTypes.map((lt) => {
                          return (
                            <ToggleButton
                              key={lt}
                              value={lt}
                              sx={{ pr: "12px", pl: "12px" }}
                            >
                              <Stack
                                direction="row"
                                spacing={{ xs: 1 }}
                                useFlexGap
                              >
                                {field.value === lt && <CheckIcon />}
                                {listTypeLabel[lt]}
                              </Stack>
                            </ToggleButton>
                          );
                        })}
                      </HigherContrastToggleButtonGroup>
                    </FormControl>
                  );
                }}
                name={"type"}
              />
            )}
            <Collapse
              in={type === ListType.Prohibited && !!patron?.children}
              sx={{ mt: 2 }}
            >
              <ChildCheckboxGroup<CreateListFormInput>
                label={translate("associatedStudentsDirections")}
                name="childIds"
                students={patron?.children}
              />
            </Collapse>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onClose()}>{translate("cancel")}</Button>
          <Button variant="contained" type="submit">
            {translate("createNewList")}
          </Button>
        </DialogActions>
      </form>
    </FormProvider>
  );
};

interface AddToListDialogProps {
  open: boolean;
  onClose: () => void;
  resourceIds: number[] | null | undefined;
  defaultName: string | null | undefined;
}

export const AddToListDialog: FC<AddToListDialogProps> = ({
  open,
  onClose,
  resourceIds,
  defaultName,
}) => {
  const { supportedFunctionality } = useSupportedFunctionality();
  const { useGuardianProhibition } = supportedFunctionality || {};
  const { translate } = useLanguage();
  const handleResponse = useAddToListResponseHandler();
  const {
    isConfigAdmin,
    hasChildrenAndProhibitedListFunctionality,
    isAuthenticated,
  } = useAccount();
  const isMobile = useIsMobile();
  const listTypeLabel = useListTypeLabel();
  const { trackEvent } = useAnalytics();

  const [allLists, setAllLists] = useState<AllListsSummary[] | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<ListType[]>([]);
  const [selectedList, setSelectedList] = useState<
    AllListsSummary | null | undefined
  >(null);
  const [loading, setLoading] = useState<boolean>(true);

  const [creating, setCreating] = useState<boolean>(false);
  useEffect(() => {
    // reset setCreating on open
    if (open) setCreating(false);
  }, [open]);

  const listTypes = useMemo(() => {
    if (
      hasChildrenAndProhibitedListFunctionality &&
      useGuardianProhibition &&
      isConfigAdmin
    )
      return [ListType.Prohibited, ListType.Config, ListType.Personal];
    if (hasChildrenAndProhibitedListFunctionality)
      return [ListType.Prohibited, ListType.Personal];
    if (isConfigAdmin) return [ListType.Config, ListType.Personal];
    return [ListType.Personal];
  }, [
    isConfigAdmin,
    hasChildrenAndProhibitedListFunctionality,
    useGuardianProhibition,
  ]);

  useEffect(() => {
    if (!isAuthenticated) {
      setAllLists(null);
      onClose();
      return;
    }
    if (!open) {
      return;
    }

    setLoading(true);
    ListService.getAllLists().then(({ allLists }) => {
      setAllLists(allLists);
      if (allLists && allLists.length > 0) {
        setSelectedList(allLists[0]);
      }
      setLoading(false);
    });
  }, [open, isAuthenticated]);

  const filteredListGroups = useMemo(() => {
    if (!allLists) return [];
    return listTypes
      .filter(
        (type) => appliedFilters.length === 0 || appliedFilters.includes(type),
      )
      .map((type) => ({
        type,
        items: allLists.filter((li) => li.type === type),
      }))
      .filter(({ items }) => items.length > 0);
  }, [allLists, listTypes, appliedFilters]);

  if (!open || !isAuthenticated) {
    return null;
  }

  return (
    <Dialog
      fullScreen={isMobile}
      open={open}
      onClose={() => {
        onClose();
      }}
    >
      {creating && resourceIds ? (
        <CreateListForm
          onClickBack={() => setCreating(false)}
          onClose={onClose}
          resourceIds={resourceIds}
          defaultName={defaultName}
        />
      ) : (
        <>
          <DialogTitle>{translate("addToList")}</DialogTitle>
          <DialogContent
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: isMobile ? "auto" : ADD_TO_LIST_DIALOG_WIDTH,
            }}
          >
            <Button
              loading={loading}
              variant="outlined"
              fullWidth
              onClick={() => setCreating(true)}
            >
              {translate("createNewList")}
            </Button>
            <Typography>{translate("addToListControl_header")}</Typography>
            {!loading && listTypes.length > 1 && (
              <Stack
                direction="row"
                spacing={1}
                sx={{ flexWrap: "wrap" }}
                useFlexGap
              >
                <Chip
                  sx={{ textTransform: "capitalize" }}
                  label={`${translate("all")} (${(allLists || []).length})`}
                  onClick={() => {
                    setAppliedFilters([]);
                  }}
                  variant="outlined"
                  color={appliedFilters.length === 0 ? "secondary" : "default"}
                  icon={appliedFilters.length === 0 ? <CheckIcon /> : <></>}
                />
                {listTypes.map((lt) => {
                  const label = listTypeLabel[lt];
                  const listCount = (allLists || []).filter(
                    (l) => l.type === lt,
                  ).length;
                  const selected = appliedFilters.includes(lt);
                  return (
                    <Chip
                      key={lt}
                      onClick={() => {
                        setAppliedFilters((prev) => {
                          if (prev.includes(lt)) {
                            return prev.filter((f) => f !== lt);
                          }
                          return [...prev, lt];
                        });
                      }}
                      variant="outlined"
                      label={`${label} (${listCount})`}
                      disabled={listCount === 0}
                      color={selected ? "secondary" : "default"}
                      icon={selected ? <CheckIcon /> : <></>}
                    />
                  );
                })}
              </Stack>
            )}
            <List
              disablePadding
              role="radiogroup"
              aria-label={translate("addToList")}
            >
              {!loading &&
                filteredListGroups.map(({ type, items }) => {
                  return (
                    <React.Fragment key={type}>
                      {listTypes.length > 1 && (
                        <ListSubheader component="div">
                          {listTypeLabel[type]}
                        </ListSubheader>
                      )}
                      {items.map((li) => {
                        const selected = li.id === selectedList?.id;
                        const count = li.count || 0; // count can be null for default list
                        return (
                          <ListItemButton
                            role="radio"
                            key={li.id}
                            selected={selected}
                            aria-checked={selected}
                            aria-labelledby={`list-option-${li.id}`}
                            onClick={() => setSelectedList(li)}
                            component="div"
                          >
                            {/* count can be null for default list */}
                            <ListItemText
                              id={`list-option-${li.id}`}
                              primary={li.name}
                              secondary={`${count} ${translate(
                                count === 1 ? "Resource" : "resources",
                              )}`}
                            ></ListItemText>
                            <ListItemIcon>
                              {selected ? (
                                <RadioButtonCheckedIcon color="primary" />
                              ) : (
                                <RadioButtonUncheckedIcon color="action" />
                              )}
                            </ListItemIcon>
                          </ListItemButton>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => onClose()}>{translate("cancel")}</Button>
            <Button
              variant="contained"
              onClick={async () => {
                if (!selectedList || !resourceIds) return;
                // !selectedList.id is the default list. we really need to deprecate this behavior
                let response;
                if (!selectedList?.id) {
                  response = await ListService.createList(
                    selectedList.name,
                    selectedList.type,
                    resourceIds,
                  );
                  trackEvent("list_create", {
                    list_type: selectedList.type,
                    item_count: resourceIds.length,
                    default_list: true,
                    batch: resourceIds.length > 1,
                  });
                } else {
                  response = await ListService.addToList(
                    selectedList.id,
                    selectedList.type,
                    resourceIds,
                  );
                  trackEvent("list_add_item", {
                    list_type: selectedList.type,
                    item_count: resourceIds.length, // potentially high cardinality, consider bucketing
                    batch: resourceIds.length > 1,
                  });
                }
                handleResponse(response);
                onClose();
              }}
            >
              {translate("addToList")}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

interface AddToListDialogContextType {
  showDialog: (resourceIds: number[], defaultName?: string) => void;
}

const addToListDialogContext = createContext<
  AddToListDialogContextType | undefined
>(undefined);

export const useAddToListDialog = (): AddToListDialogContextType => {
  const context = useContext(addToListDialogContext);
  if (!context)
    throw new Error(
      "useAddToListDialog must be used within a AddToListDialogProvider",
    );
  return context;
};

export const AddToListDialogProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [resourceIds, setResourceIds] = useState<number[] | null | undefined>();
  const [defaultName, setDefaultName] = useState<string | null>();

  const { openDialog, closeDialog, open } = useDialog();

  //   TODO: return a Promise with the result of the dialog?
  const showDialog = useCallback(
    (resourceIds: number[], defaultName?: string) => {
      if (!resourceIds || !Array.isArray(resourceIds) || !resourceIds.length) {
        console.warn(
          "AddToListDialogProvider.showDialog called without resourceIds",
        );
        return;
      }
      setDefaultName(defaultName || null);
      setResourceIds(resourceIds);
      // TODO: replace promptIfNotLoggedIn this with a context/provider
      LoginClientManager.promptIfNotLoggedIn(() => {
        openDialog();
      });
    },
    [],
  );

  const handleClose = useCallback(() => {
    closeDialog();
  }, []);

  return (
    <addToListDialogContext.Provider value={{ showDialog }}>
      <AddToListDialog
        resourceIds={resourceIds}
        open={open}
        onClose={handleClose}
        defaultName={defaultName}
      />
      {children}
    </addToListDialogContext.Provider>
  );
};
